"use client";
import {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,} from 'react';

const CartContext = createContext(null);

export function CartProvider({ children }) {
    const VAT_RATE = 0.20;
    const cartQueue = useRef([]);
    const channelRef = useRef(null);
    const skipBroadcast = useRef(false);
    const processingQueue = useRef(false);
    const [cart, setCart] = useState(null)
    const [cartItems, setCartItems] = useState([])
    const [loading, setLoading] = useState(true)
    const [cartLoading, setCartLoading] = useState(false)
    const [sidecartOpen, setSidecartOpen] = useState(false)
    const [updatingVariantId, setUpdatingVariantId,] = useState(null)
    const [shopSettings, setShopSettings] = useState(null);
    const [shippingMethods,setShippingMethods] = useState([]);
    const [paymentMethods,setPaymentMethods] = useState([]);
    const [selectedShippingMethod, setSelectedShippingMethod] = useState(null)
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null)
    const freeDeliveryShippingMethod = shippingMethods?.[0] || null;
    const [orderNote, setOrderNote] = useState(cart?.notes || '');

    // NEW — coupon apply/remove state
    const [couponLoading, setCouponLoading] = useState(false);
    const [couponError, setCouponError] = useState('');

    const allowFreeDelivery =
        freeDeliveryShippingMethod &&
        selectedShippingMethod?.ship_method_id ===
        freeDeliveryShippingMethod.ship_method_id;

    const shippingProtectionEnabled = shopSettings?.shipping_protection_enabled || false;
    const protectionSelected = cart?.shipping_protection_enabled ?? shippingProtectionEnabled;
    const shippingProtectionFee = Number(shopSettings?.shipping_protection_fee || 0);
    const [shippingProtection, setShippingProtection] = useState(false);

    useEffect(()=>{
        let ignore = false
        async function fetchData(){
            try {
                const [settingRes,shippingRes,paymentRes,] = await Promise.all([
                    fetch('/api/shop/setting'),
                    fetch('/api/shipping'),
                    fetch('/api/paymethods'),
                ]);
                const settingData = await settingRes.json()
                const shippingData = await shippingRes.json()
                const paymentData = await paymentRes.json()
                if(!ignore){
                    setShopSettings(settingData.setting || {})
                    setShippingMethods(shippingData.data || [])
                    setPaymentMethods(paymentData.data || [])
                }
            } catch (e){
                console.log(e)
            }
        }
        fetchData()
        return () => {
            ignore = true
        }
    },[])

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setShippingProtection(protectionSelected);
        // eslint-enable-next-line react-hooks/set-state-in-effect
    }, [protectionSelected]);

    const vatIncluded = shopSettings?.vat_included ?? true;
    const minCheckoutPrice = shopSettings?.min_checkout_price || 0.99;

    const dispatchCartEvent = useCallback(async (event) => {
        try {
            const resp = await fetch('/api/cart/event', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(event),
            });
            const data = await resp.json();
            if (data.success && data.cart) {
                setCart(data.cart);
                setCartItems(data.cartItems || [])
                skipBroadcast.current = true;
                channelRef.current?.postMessage({
                    type: "cart_update",
                    cart: data.cart,
                    cartItems: data.cartItems,
                });
            }
            return data;
        } catch (e) {
            console.error("Cart event failed", e);
            throw e;
        }
    },[])

    const processQueue = async () => {
        if (processingQueue.current) return;
        processingQueue.current = true;
        while (cartQueue.current.length > 0) {
            const { event, resolve, reject } = cartQueue.current.shift();
            try {
                const data = await dispatchCartEvent(event);
                resolve(data);
            } catch (e) {
                reject(e);
            }
        }
        processingQueue.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const enqueueCartEvent = (event) => {
        return new Promise((resolve, reject) => {
            cartQueue.current.push({ event, resolve, reject });
            processQueue();
        });
    };
    // eslint-enable-next-line react-hooks/exhaustive-deps

    const previousCartId = useRef(null);

    const loadCart = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch('/api/cart')
            const data = await response.json()
            if (previousCartId.current && previousCartId.current !== data?.cart?.acart_id) {
                syncedFromCart.current = false;
            }
            previousCartId.current = data?.cart?.acart_id;

            setCartItems(data.cartItems || [])
            setCart(data.cart || null)
        } catch (error) {
            console.error(error)
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        loadCart()
        // eslint-enable-next-line react-hooks/set-state-in-effect
    }, [loadCart])

    const addToCart = useCallback(async (payload) => {
        if (cartLoading) return
        setCartLoading(true)
        try {
            await dispatchCartEvent({
                type: 'add',
                ...payload,
            });
            setSidecartOpen(true)
        } finally {
            setCartLoading(false)
        }
    }, [cartLoading,dispatchCartEvent])

    const updateCartItem = useCallback(async ({ variant_id, newQty }) => {
        if (cartLoading) return
        setCartLoading(true)
        setUpdatingVariantId(variant_id);
            try {
                await enqueueCartEvent({
                    type: 'update',
                    variant_id,
                    newQty,
                });
            } finally {
                setCartLoading(false)
                setUpdatingVariantId(null);
            }
        }, [cartLoading,enqueueCartEvent]);

    const removeCartItem = useCallback(async (payload) => {
        if (cartLoading) return
        setCartLoading(true)
        try {
            await dispatchCartEvent({
                type: 'remove',
                ...payload,
            })
        } finally {
            setCartLoading(false)
        }

    }, [cartLoading,dispatchCartEvent])

    // NEW — apply/remove coupon via the same event pipeline as every other
    // cart mutation, so cart/cartItems state and cross-tab BroadcastChannel
    // sync all come for free from dispatchCartEvent.
    const applyCoupon = useCallback(async (code) => {
        const trimmed = code?.trim();
        if (!trimmed) {
            setCouponError('Enter a coupon code');
            return { success: false, message: 'Enter a coupon code' };
        }
        setCouponLoading(true);
        setCouponError('');
        try {
            const data = await dispatchCartEvent({
                type: 'coupon_applied',
                code: trimmed.toUpperCase(),
            });
            if (!data.success) {
                setCouponError(data.message || 'Invalid coupon code');
            }
            return data;
        } finally {
            setCouponLoading(false);
        }
    }, [dispatchCartEvent]);

    const removeCoupon = useCallback(async (code) => {
        setCouponLoading(true);
        setCouponError('');
        try {
            const data = await dispatchCartEvent({
                type: 'coupon_removed',
                code,
            });
            if (!data.success) {
                setCouponError(data.message || 'Could not remove coupon');
            }
            return data;
        } finally {
            setCouponLoading(false);
        }
    }, [dispatchCartEvent]);

    const cartCount = useMemo(() => {
        return cartItems.reduce(
            (total, item) => {
                return (
                    total + item.quantity
                )
            },
            0
        )
    }, [cartItems])

    const cartSummary = useMemo(()=>{
        return {
            subtotal:cart?.subtotal,
            shipping:cart?.shipping_cost,
            vat:cart?.tax_amount,
            discount:cart?.discount_amount,
            total:cart?.cart_total,
            applied_coupons:cart?.applied_coupons || [],
            rule_discounts: cart?.rule_discounts || [],
        }
    },[cart])

    const freeDeliveryAmount = Number(shopSettings?.free_delivery_amount || 0);
    const cartSubtotal = Number(cartSummary?.subtotal || 0);
    const qualifiesForFreeDelivery = freeDeliveryAmount > 0 && cartSubtotal >= freeDeliveryAmount;
    const remainingForFreeDelivery = Math.max(0, freeDeliveryAmount - cartSubtotal);
    const freeDeliveryProgress = freeDeliveryAmount > 0
            ? Math.min((cartSubtotal / freeDeliveryAmount) * 100, 100)
            : 0;

    const shippingCost = useMemo(() => {
        if (!selectedShippingMethod) {return 0;}
        if (qualifiesForFreeDelivery && allowFreeDelivery) {return 0;}
        return Number(selectedShippingMethod.price || 0);
    }, [selectedShippingMethod, qualifiesForFreeDelivery,allowFreeDelivery]);

    const paymentFee = Number(cart?.payment_fee || 0);

    const syncedFromCart = useRef(false);

    useEffect(() => {
        if (syncedFromCart.current || !cart || !paymentMethods.length || !shippingMethods.length) {
            return;
        }

        const existingPayment = paymentMethods.find(p => p.payment_name === cart.payment_method);

        const existingShipping = shippingMethods.find(s => s.method === cart.shipping_method);

        if (existingPayment) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setSelectedPaymentMethod(existingPayment);
            // eslint-enable-next-line react-hooks/set-state-in-effect
        }

        if (existingShipping) {
            setSelectedShippingMethod(existingShipping);
        }

        // eslint-disable-next-line react-hooks/immutability
        syncedFromCart.current = true;
        // eslint-enable-next-line react-hooks/immutability

    }, [cart, paymentMethods, shippingMethods]);

    const updatePaymentMethod = useCallback(async () => {
        if (!selectedPaymentMethod) {return;}
        if (cart?.payment_method === selectedPaymentMethod.payment_name
        ) {return;}
        try {
            await dispatchCartEvent({
                type: "payment_selected",
                payment_method: selectedPaymentMethod.payment_name,
            });
        } catch (e) {
            console.error("Payment Method Select event failed", e);
        }
    }, [selectedPaymentMethod,cart?.payment_method,dispatchCartEvent]);

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        updatePaymentMethod();
        // eslint-enable-next-line react-hooks/set-state-in-effect
    }, [updatePaymentMethod]);

    const updateShippingMethod = useCallback(async () => {
        if (!selectedShippingMethod) {return;}
        if (cart?.shipping_method === selectedShippingMethod.method) {return;}
        try {
            await dispatchCartEvent({
                type: "shipping_selected",
                shipping_method: selectedShippingMethod.method,
                shipping_cost: shippingCost,
            });
        } catch (e) {
            console.error("Shipping Method Select event failed", e);
        }
    }, [selectedShippingMethod, shippingCost, dispatchCartEvent,cart?.shipping_method]);

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        updateShippingMethod();
        // eslint-enable-next-line react-hooks/set-state-in-effect
    }, [updateShippingMethod]);

    const preTaxTotal =
        Number(cart?.subtotal || 0)
        - Number(cart?.discount_amount || 0)
        + Number(cart?.shipping_cost || 0)
        + Number(cart?.payment_fee || 0)
        + Number(cart?.shipping_protection_fee || 0);

    const orderSummary = useMemo(()=>{
        return {
            subtotal: cart?.subtotal || 0,
            discount: cart?.discount_amount || 0,
            shipping: cart?.shipping_cost || 0,
            paymentFee: cart?.payment_fee || 0,
            shippingProtection: cart?.shipping_protection_fee || 0,
            tax: cart?.tax_amount || 0,
            preTaxTotal,
            total: cart?.cart_total || 0,
        }
    },[cart,preTaxTotal])

    const paymentName = selectedPaymentMethod?.payment_name || null;


    const handleProtectionChange = useCallback(async (checked) => {
        if (cart?.shipping_protection_enabled === checked) {
            setShippingProtection(checked);
            return;
        }
        setShippingProtection(checked);
       await dispatchCartEvent({
            type: 'shipping_protection_updated',
            shipping_protection_enabled: checked,
            shipping_protection_fee: checked
                ? shippingProtectionFee
                : 0,
        });

    }, [cart?.shipping_protection_enabled,dispatchCartEvent, shippingProtectionFee]);

    const clearCart = useCallback(() => {
        // eslint-disable-next-line react-hooks/immutability
        syncedFromCart.current = false;
        // eslint-enable-next-line react-hooks/immutability
        setCart(null);
        setCartItems([]);
        setSelectedPaymentMethod(null);
        setSelectedShippingMethod(null);
    }, []);

    useEffect(() => {
        if (typeof window === "undefined") return;

        channelRef.current = new BroadcastChannel("cart_updates");

        channelRef.current.onmessage = (event) => {
            switch (event.data.type) {
                case "cart_update":
                    setCart(event.data.cart);
                    setCartItems(event.data.cartItems || []);
                    break;

                case "cart_cleared":
                    clearCart();
                    break;

                default:
                    break;
            }
        };

        return () => {
            channelRef.current?.close();
        };
    }, [clearCart]);

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setOrderNote(cart?.notes || '');
        // eslint-enable-next-line react-hooks/set-state-in-effect
    }, [cart?.notes]);

    const value = useMemo(() => ({
        cart,
        cartItems,
        dispatchCartEvent,
        loading,
        cartLoading,
        sidecartOpen,
        setSidecartOpen,
        loadCart,
        addToCart,
        removeCartItem,
        updateCartItem,
        updatingVariantId,
        cartCount,
        cartSummary,
        VAT_RATE,
        vatIncluded,
        shippingMethods,
        paymentMethods,
        shopSettings,
        shippingCost,
        selectedShippingMethod,
        setSelectedShippingMethod,
        selectedPaymentMethod,
        setSelectedPaymentMethod,
        paymentFee,
        orderSummary,
        freeDeliveryAmount,
        qualifiesForFreeDelivery,
        remainingForFreeDelivery,
        freeDeliveryProgress,
        allowFreeDelivery,
        freeDeliveryShippingMethod,
        paymentName,
        shippingProtectionEnabled,
        shippingProtection,
        shippingProtectionFee,
        handleProtectionChange,
        clearCart,
        orderNote,
        setOrderNote,
        applyCoupon,
        removeCoupon,
        couponLoading,
        couponError,
        minCheckoutPrice,
    }), [
        cart,
        cartItems,
        dispatchCartEvent,
        loading,
        cartLoading,
        sidecartOpen,
        loadCart,
        addToCart,
        removeCartItem,
        updateCartItem,
        updatingVariantId,
        cartCount,
        cartSummary,
        VAT_RATE,
        vatIncluded,
        shippingMethods,
        paymentMethods,
        shopSettings,
        shippingCost,
        selectedShippingMethod,
        setSelectedShippingMethod,
        selectedPaymentMethod,
        setSelectedPaymentMethod,
        paymentFee,
        orderSummary,
        freeDeliveryAmount,
        qualifiesForFreeDelivery,
        remainingForFreeDelivery,
        freeDeliveryProgress,
        allowFreeDelivery,
        freeDeliveryShippingMethod,
        paymentName,
        shippingProtectionEnabled,
        shippingProtection,
        shippingProtectionFee,
        handleProtectionChange,
        clearCart,
        orderNote,
        setOrderNote,
        applyCoupon,
        removeCoupon,
        couponLoading,
        couponError,
        minCheckoutPrice,
    ])

    return (
        <CartContext.Provider value={value}>
            {children}
        </CartContext.Provider>
    )
}

export function useCart() {
    return useContext(CartContext)
}
